import { Accordion, AccordionSummary, Box, Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, OutlinedInput, Select, Stack } from "@mui/material";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ParentScreenCtx } from "../../../../pages/workbench_build/WorkbenchBuild";
import { eachDeep } from "deepdash-es/standalone";
import { cloneDeep, debounce, get, isArray, isEqual, min, set } from "lodash";
import { api } from "../../../../redux/api";
import TDataInboundDependency from "./TDataInboundDependency";
import axios from "axios";
import InboundConditionModal from "./InboundConditionModal";

export default function InboundConditionControls({treeSelectedNode, setTreeSelectedNode, treeData, setTreeData}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const parentScreen = useContext(ParentScreenCtx);
  const { rightTree, leftTree } = useSelector(state => state.treeData[parentScreen]);
  const { translatedData } = rightTree.tree.body;
  const selectedNode = rightTree.tree.body.selectedNode;
  const { baseUrl, user, token, account } = useSelector(state => state.queryParams);

  const inboundTemplatesTree = useSelector(
    (state) => state.treeData.inbound.leftTree.tree.body.translatedData
  );

  //main save function. clone data, find node in tdata, change obj, dispatch new tdata and selnode
  const submitChange = (key, value, bulk, removeValues) => {
    if (!key && !removeValues && !bulk) return;
    // Clone and deeply iterate through the tree data to update the relevant node.
    const updatedData = eachDeep(cloneDeep(treeData), (obj) => {
      // if (obj?.key !== treeSelectedNode?.key) return;

      if (obj?.key === treeSelectedNode?.key) {
        if (bulk && bulk.length > 0) {
          //bulk received from InboundConditionModal save
          bulk.forEach((item) => {
            if (item.value === null || item.value === undefined || item.value === "") { 
              if (obj?.hasOwnProperty(item.key)) delete obj[item.key];
            } else obj[item.key] = item.value;
          });
        } else {
          //not bulk, individual key/value pair
          if (key === "NAME") {
            if (value === "" || value === null || value === undefined) {
              obj.NAME = "UNNAMED";
            } else {
              obj.NAME = value;
            }
  
          } else if (key === "VALIDATE_DATA") {
            if (value === "true") {
              obj.VALIDATE_DATA = "true";
              obj.ELEMENT_VALIDATE_DATA = "true";
              obj.SEGMENT_VALIDATE_DATA = "true";
              obj.TRANSACTIONSET_VALIDATE_DATA = "true";
              obj.FUNCTIONAL_GROUP_VALIDATE_DATA = "true";
            } else {
              if (obj?.hasOwnProperty('VALIDATE_DATA')) delete obj['VALIDATE_DATA'];
              if (obj?.hasOwnProperty('ELEMENT_VALIDATE_DATA')) delete obj['ELEMENT_VALIDATE_DATA'];
              if (obj?.hasOwnProperty('SEGMENT_VALIDATE_DATA')) delete obj['SEGMENT_VALIDATE_DATA'];
              if (obj?.hasOwnProperty('TRANSACTIONSET_VALIDATE_DATA')) delete obj['TRANSACTIONSET_VALIDATE_DATA'];
              if (obj?.hasOwnProperty('FUNCTIONAL_GROUP_VALIDATE_DATA')) delete obj['FUNCTIONAL_GROUP_VALIDATE_DATA'];
              if (obj?.hasOwnProperty('STOP_ON_FAILED_INBOUND')) delete obj['STOP_ON_FAILED_INBOUND'];
            }
          } else {
            // Update other properties directly on the object.
            if (value === null) {
              if (obj?.hasOwnProperty(key)) delete obj[key];
            } else obj[key] = value;
          }
        }
        setTreeSelectedNode(obj);
      } else {
        if (key === "DEFAULT") {
          // if not the seleceted node, and key is DEFAULT, set set to FALSE
          if (obj.DEFAULT === "TRUE") obj.DEFAULT = "FALSE";
        }
      }

      

      // If the object has children, return them for further iteration.
      if (isArray(obj?.children)) {
        return obj.children;
      }
    });
    setTreeData(updatedData);
  };
  
  const debounceChangeHandler = useCallback(debounce(submitChange, 100), [rightTree.tree]);

  //*** "Name" - NAME - text field ***//
  const defaultName = treeSelectedNode?.NAME || "";
  const [name, setName] = useState(defaultName);

  // Handle changes to the data name, specific logic for onChange event
  const handleNameChange = (e) => {
    if (e?.target?.value || e?.target?.value === '') {
      setName(e.target.value);
      if (e.target.value !== '') debounceChangeHandler('NAME', e.target.value);
    }
  }

  // Handle changes to the data name, specific logic for onBlur event
  const handleNameBlur = (eVal, e) => {
    let newName = eVal;
    if (eVal === '' || eVal === undefined || eVal === null) {
      newName = "UNNAMED";
    }

    if (newName !== eVal) {
      setName(newName);
      debounceChangeHandler('NAME', newName);
    }
  }

  //*** "Set as Default Inbound Condition" - DEFAULT - bool checkbox ***//
  const defaultDefaultChecked = treeSelectedNode?.DEFAULT === "TRUE" ? true : false;
  const [defaultChecked, setDefaultChecked] = useState(defaultDefaultChecked);

  const handleDefaultChange = (e) => {
    const checked = e?.target?.checked;
    setDefaultChecked(checked);
    debounceChangeHandler('DEFAULT', checked ? 'TRUE' : 'FALSE');
  }

  //*** checkboxes logic ***//
  //DATABASE disable checkboxes and disable Inbound Condition Properties
  //XML disable checkboxes

  //Suppress Inbound Record Mapping Error - IGNORE_INBOUND_DATA_MAPPING_ERROR - bool checkbox - LBL_IGNORE_IN_DATA_MAPPING_ERR
  //Validate Inbound Data - VALIDATE_DATA - bool checkbox - LBL_VAL_IN_DATA
  //subitems if datatype EDI:
  //    Element Validation - ELEMENT_VALIDATE_DATA - bool checkbox - LBL_VAL_ELEMENT
  //    Segment Validation - SEGMENT_VALIDATE_DATA - bool checkbox - LBL_VAL_SEGMENT
  //    Transaction Set Validation - TRANSACTIONSET_VALIDATE_DATA - bool checkbox - LBL_VAL_TRANSACTIONSET
  //    Functional Group Validation - FUNCTIONAL_GROUP_VALIDATE_DATA - bool checkbox - LBL_VAL_FUNCTIONAL_GROUP
  //Stop Translation on Eror - STOP_ON_FAILED_INBOUND - bool checkbox - LBL_STOP_TRANSLATING_ON_ERROR

  //datatype defines what checkboxes are available
  const [templateDatatype, setTemplateDatatype] = useState("");
  // console.log("templateDatatype", templateDatatype);

  //*** "Suppress Inbound Record Mapping Error" - IGNORE_INBOUND_DATA_MAPPING_ERROR - bool checkbox ***//
  const defaultsuppressIbErrChecked = treeSelectedNode?.IGNORE_INBOUND_DATA_MAPPING_ERROR === "true" ? true : false;
  const [suppressIbErrChecked, setSuppressIbErrChecked] = useState(defaultsuppressIbErrChecked);

  const handleSuppressIbErrChange = (e) => {
    const checked = e?.target?.checked;
    setSuppressIbErrChecked(checked);
    debounceChangeHandler('IGNORE_INBOUND_DATA_MAPPING_ERROR', checked ? 'true' : null);
  }

  //*** "Validate Inbound Data" - VALIDATE_DATA - bool checkbox ***//
  const defaultValidateDataChecked = treeSelectedNode?.VALIDATE_DATA === "true" ? true : false;
  const [validateDataChecked, setValidateDataChecked] = useState(defaultValidateDataChecked);

  const handleValidateDataChange = (e) => {
    const checked = e?.target?.checked;
    setValidateDataChecked(checked);
    debounceChangeHandler('VALIDATE_DATA', checked ? 'true' : null);
    if (checked) {
      setElementValidateChecked(true);
      setSegmentValidateChecked(true);
      setTransactionSetValidateChecked(true);
      setFunctionalGroupValidateChecked(true);
    } else {
      setElementValidateChecked(false);
      setSegmentValidateChecked(false);
      setTransactionSetValidateChecked(false);
      setFunctionalGroupValidateChecked(false);
      setStopOnFailedChecked(false);
    }
  }

  //*** "Element Validation" - ELEMENT_VALIDATE_DATA - bool checkbox ***//
  const defaultElementValidateChecked = treeSelectedNode?.ELEMENT_VALIDATE_DATA === "true" ? true : false;
  const [elementValidateChecked, setElementValidateChecked] = useState(defaultElementValidateChecked);

  const handleElementValidateChange = (e) => {
    const checked = e?.target?.checked;
    setElementValidateChecked(checked);
    debounceChangeHandler('ELEMENT_VALIDATE_DATA', checked ? 'true' : null);
  }

  //*** "Segment Validation" - SEGMENT_VALIDATE_DATA - bool checkbox ***//
  const defaultSegmentValidateChecked = treeSelectedNode?.SEGMENT_VALIDATE_DATA === "true" ? true : false;
  const [segmentValidateChecked, setSegmentValidateChecked] = useState(defaultSegmentValidateChecked);

  const handleSegmentValidateChange = (e) => {
    const checked = e?.target?.checked;
    setSegmentValidateChecked(checked);
    debounceChangeHandler('SEGMENT_VALIDATE_DATA', checked ? 'true' : null);
  }

  //*** "Transaction Set Validation" - TRANSACTIONSET_VALIDATE_DATA - bool checkbox ***//
  const defaultTransactionSetValidateChecked = treeSelectedNode?.TRANSACTIONSET_VALIDATE_DATA === "true" ? true : false;
  const [transactionSetValidateChecked, setTransactionSetValidateChecked] = useState(defaultTransactionSetValidateChecked);

  const handleTransactionSetValidateChange = (e) => {
    const checked = e?.target?.checked;
    setTransactionSetValidateChecked(checked);
    debounceChangeHandler('TRANSACTIONSET_VALIDATE_DATA', checked ? 'true' : null);
  }

  //*** "Functional Group Validation" - FUNCTIONAL_GROUP_VALIDATE_DATA - bool checkbox ***//
  const defaultFunctionalGroupValidateChecked = treeSelectedNode?.FUNCTIONAL_GROUP_VALIDATE_DATA === "true" ? true : false;
  const [functionalGroupValidateChecked, setFunctionalGroupValidateChecked] = useState(defaultFunctionalGroupValidateChecked);

  const handleFunctionalGroupValidateChange = (e) => {
    const checked = e?.target?.checked;
    setFunctionalGroupValidateChecked(checked);
    debounceChangeHandler('FUNCTIONAL_GROUP_VALIDATE_DATA', checked ? 'true' : null);
  }

  //*** "Stop Translation on Error" - STOP_ON_FAILED_INBOUND - bool checkbox ***//
  const defaultStopOnFailedChecked = treeSelectedNode?.STOP_ON_FAILED_INBOUND === "true" ? true : false;
  const [stopOnFailedChecked, setStopOnFailedChecked] = useState(defaultStopOnFailedChecked);

  const handleStopOnFailedChange = (e) => {
    const checked = e?.target?.checked;
    setStopOnFailedChecked(checked);
    debounceChangeHandler('STOP_ON_FAILED_INBOUND', checked ? 'true' : null);
  }

  const handleInboundModalSave = (newData) => {
    const bulkData = Object.keys(newData.add).map(key => ({
      key,
      value: newData.add[key]
    }));

    if (newData.remove) {
      Object.keys(newData.remove).forEach(key => {
        bulkData.push({ key, value: null });
      });
    }
    submitChange(null, null, bulkData);
  };

  // Check if a valid template is selected and actually exists
  const isValidTemplateSelected = () => {
    const templateGroupValue = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1];
    const templateValue = treeSelectedNode?.TEMPLATE?.endsWith('.json') ? treeSelectedNode?.TEMPLATE?.slice(0, -5) : treeSelectedNode?.TEMPLATE;
    
    if (!templateGroupValue || 
        !templateValue || 
        ["SELECT", "EMPTY_COMBO", ""].includes(templateValue) ||
        templateDatatype === "") {
      return false;
    }

    // Check if template actually exists in available templates
    const templatesForWfGroup = inboundTemplatesTree?.filter((node) => node?.type === "workflowGroup" && node?.title === templateGroupValue)[0]?.children || [];
    const actualTemplates = templatesForWfGroup?.filter((e) => e.type === "inboundTemplate") || [];
    const availableTemplateNames = actualTemplates?.map(ele => ele.title.endsWith('.json') ? ele.title.slice(0, -5) : ele.title) || [];
    
    return availableTemplateNames.includes(templateValue);
  };

  const validTemplateSelected = isValidTemplateSelected();

  // Update all state variables when treeSelectedNode changes
  useEffect(() => {
    setName(treeSelectedNode?.NAME || "");
    setDefaultChecked(treeSelectedNode?.DEFAULT === "TRUE");
    setSuppressIbErrChecked(treeSelectedNode?.IGNORE_INBOUND_DATA_MAPPING_ERROR === "true");
    setValidateDataChecked(treeSelectedNode?.VALIDATE_DATA === "true");
    setElementValidateChecked(treeSelectedNode?.ELEMENT_VALIDATE_DATA === "true");
    setSegmentValidateChecked(treeSelectedNode?.SEGMENT_VALIDATE_DATA === "true");
    setTransactionSetValidateChecked(treeSelectedNode?.TRANSACTIONSET_VALIDATE_DATA === "true");
    setFunctionalGroupValidateChecked(treeSelectedNode?.FUNCTIONAL_GROUP_VALIDATE_DATA === "true");
    setStopOnFailedChecked(treeSelectedNode?.STOP_ON_FAILED_INBOUND === "true");
  }, [treeSelectedNode]);

  useEffect(() => {
    //we have to fetch the template data from the server to know what datatype it is. can't find this info in the inbound WFGROUP tree data
    const templateGroupValue = treeSelectedNode?.TEMPLATE_LOCATION?.split("/")?.[1];
    const templateValue = treeSelectedNode?.TEMPLATE?.endsWith('.json') ? treeSelectedNode?.TEMPLATE?.slice(0, -5) : treeSelectedNode?.TEMPLATE;

    if (
      templateGroupValue 
      && templateValue 
      && !["SELECT", "EMPTY_COMBO", ""].includes(templateValue)
    ) {
      axios.get(`${baseUrl}/workbench`, {
        params: {
          action: 'get_inbound_template_datatype',
          user: user,
          account: account,
          workflow_group_name: templateGroupValue,
          template_name: templateValue,
        },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }).then((res) => {
        if (res?.data?.datatype && res?.data?.datatype !== "null") {
          setTemplateDatatype(res.data.datatype);
        } else {
          setTemplateDatatype("");
        }
      }).catch((err) => {
        console.warn(err);
        setTemplateDatatype("");
      });
    } else {
      setTemplateDatatype("");
    }
  }, [treeSelectedNode.TEMPLATE_LOCATION, treeSelectedNode.TEMPLATE, treeSelectedNode.tgval, treeSelectedNode.tval]);


  return (
    <div style={{marginTop: "20px"}}>

      {/* "Name" text field */}
      <FormControl variant='outlined' className='width-100' size='small'>
        <InputLabel
          htmlFor='ib-cond-name-input'
          sx={{ padding: "0 6px !important", left: "-5px" }}
        >
          {t('LBL_IN_CONDITION_NAME').replace(":", "")}
        </InputLabel>
        <OutlinedInput
          id="ib-cond-name-input"
          size='small'
          variant='outlined'
          fullWidth
          autoComplete='off'
          value={name}
          // endAdornment={<div style={styles.getDataNameAdornment}>{`ID: ${wfItemId}`}</div>}
          // sx={{ justifyContent: "flex-end" }}
          onChange={(e) => handleNameChange(e)}
          onBlur={(e) => handleNameBlur(e?.target?.value, e)}
        />
      </FormControl>

      {/* "Set as Default Inbound Condition" checkbox */}
      {treeData?.length > 1 && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={defaultChecked}
                onChange={handleDefaultChange}
                name='defaultChecked'
              />
            }
            label={t('BTN_SET_AS_DEFAULT_IN_CONDITION')}
          />
        </FormControl>
      )}
      

      {/* Selected Inbound Template to Use */}
      <div style={{marginTop: "10px"}}>
        <div style={{fontSize: "12px", color: "#000", marginBottom: "10px"}}>{t('LBL_SEL_IN_TEMP_TO_USE')}</div>
        <TDataInboundDependency 
          treeData={treeData} 
          setTreeData={setTreeData} 
          treeSelectedNode={treeSelectedNode}
          setTreeSelectedNode={setTreeSelectedNode}
          templateDatatype={templateDatatype}
          setTemplateDatatype={setTemplateDatatype}
        />
      </div>
      
      {/* "Suppress Inbound Record Mapping Error" checkbox */}
      {(!['DATABASE', 'XML'].includes(templateDatatype) && templateDatatype !== "") && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={suppressIbErrChecked}
                onChange={handleSuppressIbErrChange}
                name='suppressIbErrChecked'
                disabled={!validTemplateSelected}
              />
            }
            label={t('LBL_IGNORE_IN_DATA_MAPPING_ERR')}
          />
        </FormControl>
      )}

      {/* "Validate Inbound Data" checkbox */}
      {(!['DATABASE', 'XML'].includes(templateDatatype) && templateDatatype !== "") && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={validateDataChecked}
                onChange={handleValidateDataChange}
                name='validateDataChecked'
                disabled={!validTemplateSelected}
              />
            }
            label={t('LBL_VAL_IN_DATA')}
          />
        </FormControl>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", marginLeft: "30px"}}>
        {/* "Element Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "true") && (
          <FormControl variant='outlined'size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={elementValidateChecked}
                  onChange={handleElementValidateChange}
                  name='elementValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_ELEMENT')}
            />
          </FormControl>
        )}

        {/* "Segment Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "true") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={segmentValidateChecked}
                  onChange={handleSegmentValidateChange}
                  name='segmentValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_SEGMENT')}
            />
          </FormControl>
        )}

        {/* "Transaction Set Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "true") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={transactionSetValidateChecked}
                  onChange={handleTransactionSetValidateChange}
                  name='transactionSetValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_TRANSACTIONSET')}
            />
          </FormControl>
        )}

        {/* "Functional Group Validation" checkbox */}
        {(templateDatatype === 'EDI' && treeSelectedNode?.VALIDATE_DATA === "true") && (
          <FormControl variant='outlined' size='small'>
            <FormControlLabel
              control={
                <Checkbox
                  checked={functionalGroupValidateChecked}
                  onChange={handleFunctionalGroupValidateChange}
                  name='functionalGroupValidateChecked'
                  disabled={!validTemplateSelected}
                />
              }
              label={t('LBL_VAL_FUNCTIONAL_GROUP')}
            />
          </FormControl>
        )}
      </div>

      

      {/* "Stop Translation on Error" checkbox */}
      {(!['DATABASE', 'XML'].includes(templateDatatype) && treeSelectedNode?.VALIDATE_DATA === "true") && (
        <FormControl variant='outlined' className='width-100' size='small'>
          <FormControlLabel
            control={
              <Checkbox
                checked={stopOnFailedChecked}
                onChange={handleStopOnFailedChange}
                name='stopOnFailedChecked'
                disabled={!validTemplateSelected}
              />
            }
            label={t('LBL_STOP_TRANSLATING_ON_ERROR')}
          />
        </FormControl>
      )}

      {(treeSelectedNode && treeSelectedNode.type === "inbound") && (
        <InboundConditionModal 
          node={treeSelectedNode} 
          valuesHandler={handleInboundModalSave} 
          disabled={!validTemplateSelected}
        />
      )}
    </div>
  )
}

const styles = {
  getDataNameAdornment: {
    color: "#666666",
    fontSize: "9px",
    position: "absolute",
    right: "13px",
    top: "-6px",
    backgroundColor: "#FFF",
    zIndex: "2",
    padding: "0 5px",
    height: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  treeHeaderContainer: {
    paddingTop: '8px',
    paddingBottom: '2px',
    paddingLeft: '0px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  treeContainer: {
    height: '100%',
    width: '100%',
    border: '1px solid #999',
    borderTopLeftRadius: '0px',
    borderTopRightRadius: '0px',
    borderRadius: '4px',
    minHeight: "161px",
    maxHeight: "161px", 
    overflowY: "auto"
  },
  fpContainer: {
		// padding: "0 16px 16px 16px"
		padding: "0 4px 4px 4px",
	},
	accordianContainer: {
		border: "none",
		padding: "0",
		margin: "0",
		boxShadow: "none",
		backgroundColor: "unset",
	},
	accordianHeader: {
		border: "none",
		padding: "0",
		margin: "0",
		backgroundColor: "unset",
	},
	accordianContent: {
		border: "none",
		padding: "0 0px",
		margin: "0",
		backgroundColor: "unset",
	},
};